import type { DropOptions } from '@react-aria/dnd';
import type { AriaLabelingProps, HoverEvents } from '@react-types/shared';
import type { ElementType } from 'react';
import type { VariantProps } from 'tailwind-variants';
import { useMemo, useRef } from 'react';

import { useClipboard, useDrop } from '@react-aria/dnd';
import { useFocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import { filterDOMProps, mergeProps, useLabels, useSlotId } from '@react-aria/utils';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { tv } from 'tailwind-variants';

import type { InternalComponentProps, OverrideProps } from '../../types';
import { createComponent, dataFocusVisibleClasses } from '../../utils';

const dropZoneVariants = tv({
  base: [
    'flex',
    'size-full',
    'cursor-pointer',
    'flex-col',
    'items-center',
    'justify-center',
    'p-8',
    'text-center',
    'data-[drop-target=true]:bg-gray-50',
    'data-[hover=true]:bg-gray-50',
    ...dataFocusVisibleClasses,
  ],
  variants: {
    variant: {
      bordered: [
        'rounded-sm border-1 border-dashed border-gray-500 data-[drop-target=true]:border-2 data-[drop-target=true]:border-orange-default data-[hover=true]:border-orange-default',
      ],
      ghost: '',
    },
  },
});

export interface DropZoneTypeMap<
  AdditionalProps = {},
  DefaultComponent extends ElementType = 'div',
> {
  props: AdditionalProps &
    Omit<DropOptions, 'getDropOperationForPoint' | 'ref' | 'hasDropButton'> &
    HoverEvents &
    AriaLabelingProps &
    VariantProps<typeof dropZoneVariants>;
  defaultComponent: DefaultComponent;
}
export type DropZoneProps<
  RootComponent extends ElementType = DropZoneTypeMap['defaultComponent'],
  AdditionalProps = {},
> = OverrideProps<DropZoneTypeMap<AdditionalProps, RootComponent>, RootComponent>;

type InternalDropZoneProps<AdditionalProps = {}> = InternalComponentProps<
  DropZoneTypeMap<AdditionalProps, DropZoneTypeMap['defaultComponent']>
>;

export const DropZone = createComponent<DropZoneTypeMap>((inProps: InternalDropZoneProps) => {
  const { as: Component = 'div', onDrop, variant, children, ref } = inProps;

  const buttonRef = useRef<HTMLButtonElement>(null);
  const { dropProps, dropButtonProps, isDropTarget } = useDrop({
    ...inProps,
    hasDropButton: true,
    ref: buttonRef,
  });
  const { hoverProps, isHovered } = useHover(inProps);
  const { focusProps, isFocused, isFocusVisible } = useFocusRing();

  const dropzoneId = useSlotId();
  const ariaLabel = inProps['aria-label'] ?? 'drop zone';
  const messageId = inProps['aria-labelledby'];
  // Chrome + VO will not announce the drop zone's accessible name if useLabels combines an aria-label and aria-labelledby
  const ariaLabelledby = [dropzoneId, messageId].filter(Boolean).join(' ');
  const labelProps = useLabels({ 'aria-labelledby': ariaLabelledby });

  const { clipboardProps } = useClipboard({
    onPaste: (items) =>
      onDrop?.({
        dropOperation: 'copy',
        items,
        type: 'drop',
        x: 0,
        y: 0,
      }),
  });

  const DOMProps = filterDOMProps(inProps);
  delete DOMProps.id;

  const styles = useMemo(
    () =>
      dropZoneVariants({
        variant,
      }),
    [variant],
  );

  return (
    <Component
      {...mergeProps(dropProps, hoverProps, DOMProps)}
      className={styles}
      data-drop-target={isDropTarget}
      data-focus={isFocused}
      data-focus-visible={isFocusVisible}
      data-hover={isHovered}
      onClick={() => buttonRef.current?.focus()}
      ref={ref}
    >
      <VisuallyHidden>
        {/* Added as a workaround for a Chrome + VO bug where it will not announce the aria label */}
        <div
          aria-hidden="true"
          id={dropzoneId}
        >
          {ariaLabel}
        </div>
        <button
          type="button"
          {...mergeProps(dropButtonProps, focusProps, clipboardProps, labelProps)}
          ref={buttonRef}
        />
      </VisuallyHidden>
      {children}
    </Component>
  );
});

DropZone.displayName = 'DropZone';
